import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { Moment } from 'moment';
import classNames from 'classnames';
import { findHourSlotIndex } from './hourRange';
import type { IHourSlot } from './types';

interface IHourSlotListProps {
  value?: Moment | null;
  slots: IHourSlot[];
  onChange: (slot: IHourSlot) => void;
}

const ITEM_HEIGHT = 32;

const HourSlotList: React.FC<IHourSlotListProps> = ({
  value,
  slots,
  onChange,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const selectedIndex = useMemo(() => {
    if (!value?.isValid()) return 0;
    const index = findHourSlotIndex(value, slots);
    return index >= 0 ? index : 0;
  }, [value, slots]);

  const scrollSelectedToCenter = useCallback(() => {
    const container = listRef.current;
    if (!container) return;

    const edgePadding = Math.max(
      0,
      (container.clientHeight - ITEM_HEIGHT) / 2,
    );
    container.style.paddingTop = `${edgePadding}px`;
    container.style.paddingBottom = `${edgePadding}px`;
    container.scrollTop = selectedIndex * ITEM_HEIGHT;
  }, [selectedIndex]);

  useLayoutEffect(() => {
    scrollSelectedToCenter();
  }, [scrollSelectedToCenter]);

  useEffect(() => {
    const container = listRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      scrollSelectedToCenter();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [scrollSelectedToCenter]);

  return (
    <div className="dbi-range-hour-picker__slot-list" ref={listRef}>
      {slots.map((slot) => (
        <div
          key={slot.label}
          className={classNames('dbi-range-hour-picker__slot-item', {
            'dbi-range-hour-picker__slot-item--active':
              value?.isValid() &&
              value.hour() === slot.hour &&
              value.minute() === slot.minute,
          })}
          onClick={() => onChange(slot)}
        >
          {slot.label}
        </div>
      ))}
    </div>
  );
};

export default HourSlotList;
